import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Category, Channel, FeaturedChannel, FeaturedSettings, HomeAd, HomeAds, ScheduleChannel, Screen, VodProgress, VodTitle } from '../types';
import { shortTime } from '../core/catalog';
import { ChannelCard } from './ChannelCard';
import { Icon } from './Icon';
import { GuideView } from './GuideView';

interface Props {
  channels: Channel[];
  categories: Category[];
  screen: Screen;
  onPlay: (channel: Channel) => void;
  continueWatching: VodProgress[];
  onResume: (progress: VodProgress) => void;
  homeAds: HomeAds;
  onAdImpression: (ad: HomeAd) => void;
  onAdSelect: (ad: HomeAd) => void;
  movies: VodTitle[];
  series: VodTitle[];
  onSelectVod: (title: VodTitle) => void;
  onNavigate: (screen: Screen) => void;
  featured: FeaturedChannel[];
  featuredSettings: FeaturedSettings;
  onLoadSchedules: (date: Date) => Promise<ScheduleChannel[]>;
}

function progressTime(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainder}`;
}

function featured(titles: VodTitle[]) {
  return [...titles].sort((a, b) => Number(b.highlighted) - Number(a.highlighted)).slice(0, 12);
}

function HomeVodCard({ title, onSelect }: { title: VodTitle; onSelect: (title: VodTitle) => void }) {
  return <button
    className="home-vod-card"
    data-focusable="true"
    data-focus-zone="vod-rail"
    data-focus-key={`vod-${title.id}`}
    data-testid={`home-vod-${title.id}`}
    aria-label={`${title.name}${title.year ? `, ${title.year}` : ''}`}
    onClick={() => onSelect(title)}
  >
    <img src={title.poster} alt="" loading="lazy" />
    <span className="home-vod-card__focus-title">{title.name}</span>
  </button>;
}

function SponsoredCard({ ad, onVisible, onSelect }: {
  ad: HomeAd; onVisible: (ad: HomeAd) => void; onSelect: (ad: HomeAd) => void;
}) {
  const card = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!card.current) return;
    let timer: number | undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.intersectionRatio >= .5) timer = window.setTimeout(() => onVisible(ad), 1_000);
      else if (timer) window.clearTimeout(timer);
    }, { threshold: [.5] });
    observer.observe(card.current);
    return () => { if (timer) window.clearTimeout(timer); observer.disconnect(); };
  }, [ad, onVisible]);
  const actionable = ad.destination_kind === 'channel' || ad.destination_kind === 'title';
  return <button ref={card} className="home-sponsored-card" data-focusable={actionable ? 'true' : undefined}
    data-focus-zone="vod-rail" data-testid="rail-sponsored-card" disabled={!actionable}
    onClick={() => actionable && onSelect(ad)} aria-label={`${ad.title}. Publicidade`}>
    <img src={ad.image} alt="" loading="lazy" /><span>Publicidade</span>
  </button>;
}

function RailHeader({ eyebrow, title, action, onAction }: {
  eyebrow: string; title: string; action: string; onAction: () => void;
}) {
  return <header className="home-section__header">
    <div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2></div>
    <div className="home-section__actions"><button data-focusable="true" data-focus-zone="rail-action" className="rail-link" onClick={onAction}>{action} <span>→</span></button></div>
  </header>;
}

function AdPlacement({ ad, variant, onVisible, onSelect }: {
  ad: HomeAd | null | undefined;
  variant: 'hero' | 'large' | 'wide' | 'landscape' | 'square' | 'portrait' | 'compact';
  onVisible: (ad: HomeAd) => void;
  onSelect: (ad: HomeAd) => void;
}) {
  const placement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!ad || !placement.current) return;
    let timer: number | undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.intersectionRatio >= 0.5) {
        timer = window.setTimeout(() => onVisible(ad), 1_000);
      } else if (timer) {
        window.clearTimeout(timer);
        timer = undefined;
      }
    }, { threshold: [0.5] });
    observer.observe(placement.current);
    return () => {
      if (timer) window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [ad, onVisible]);

  if (!ad) return null;
  const actionable = ad.destination_kind === 'channel' || ad.destination_kind === 'title';
  return <section ref={placement} className={`home-placement home-placement--${variant}`}
    data-testid={`home-ad-${ad.slot}`} data-ad-partner={ad.partner || 'global'}
    data-ad-format={ad.format ? `${ad.format.width}x${ad.format.height}` : 'legacy'}>
    <span>PUBLICIDADE{ad.partner_name ? ` · EXPERIÊNCIA ${ad.partner_name}` : ''}</span>
    <button data-focusable={actionable ? 'true' : undefined} tabIndex={actionable ? 0 : -1}
      onClick={() => actionable && onSelect(ad)} aria-label={ad.action_label || ad.title}>
      <img src={ad.image} alt={ad.title} />
    </button>
  </section>;
}

export function HomeScreen({
  channels, categories, screen, onPlay, continueWatching, onResume,
  homeAds, onAdImpression, onAdSelect, movies, series, onSelectVod, onNavigate,
  featured: featuredItems, featuredSettings, onLoadSchedules,
}: Props) {
  const [preview, setPreview] = useState(channels[0]);
  const [category, setCategory] = useState('ALL');
  const [collectionAd, setCollectionAd] = useState<HomeAd | null>(null);
  const impressedAds = useRef<Set<number>>(new Set());
  const visibleChannels = useMemo(() => category === 'ALL'
    ? channels
    : channels.filter(channel => channel.category.includes(category)), [category, channels]);
  const homeChannels = useMemo(() => channels.slice(0, 8), [channels]);
  const featuredMovies = useMemo(() => featured(movies), [movies]);
  const featuredSeries = useMemo(() => featured(series), [series]);
  const featuredChannels = useMemo(() => featuredItems
    .map(item => channels.find(channel => channel.id === item.channel))
    .filter((channel): channel is Channel => Boolean(channel)), [channels, featuredItems]);

  useEffect(() => {
    if (!collectionAd) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Backspace' || event.keyCode === 461) {
        event.preventDefault(); setCollectionAd(null);
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [collectionAd]);

  useEffect(() => {
    if (screen !== 'home' || featuredChannels.length < 2) return;
    const timer = window.setInterval(() => setPreview(current => {
      const index = featuredChannels.findIndex(channel => channel.id === current?.id);
      return featuredChannels[(index + 1) % featuredChannels.length];
    }), featuredSettings.rotation_seconds * 1_000);
    return () => window.clearInterval(timer);
  }, [featuredChannels, featuredSettings.rotation_seconds, preview?.id, screen]);

  const heroChannel = screen === 'home' && featuredChannels.length
    && !featuredChannels.some(channel => channel.id === preview?.id)
    ? featuredChannels[0] : preview;

  const markAdVisible = useCallback((ad: HomeAd) => {
    if (screen !== 'home' || impressedAds.current.has(ad.id)) return;
    impressedAds.current.add(ad.id);
    onAdImpression(ad);
  }, [onAdImpression, screen]);

  const primaryHeroAd = homeAds.tv_home_banner?.partner
    ? homeAds.tv_home_banner
    : homeAds.home_hero || homeAds.tv_home_banner;

  if (!preview || !heroChannel) return null;

  if (collectionAd?.collection) return <main className="content-shell sponsored-collection-view" data-testid="tv-sponsored-collection">
    <header className="topbar"><div><span className="eyebrow">CONTEÚDO PATROCINADO</span><h1>{collectionAd.title}</h1><p>Pressione Voltar para retornar à página inicial.</p></div></header>
    <div className="sponsored-collection-grid">{collectionAd.collection.items.map((item, index) => <button
      key={`${item.type}-${item.id}`} data-focusable="true" data-content-autofocus={index === 0 ? 'true' : undefined}
      onClick={() => {
        const content = item.type === 'channel' ? channels.find(channel => channel.id === item.id) : [...movies, ...series].find(title => title.id === item.id);
        if (content && item.type === 'channel') onPlay(content as Channel);
        else if (content) onSelectVod(content as VodTitle);
      }}><img src={item.image} alt="" /><strong>{item.title}</strong></button>)}</div>
    <button className="collection-back" data-focusable="true" onClick={() => setCollectionAd(null)}>← Voltar</button>
  </main>;

  if (screen === 'guide') return <main className="content-shell content-shell--guide" data-testid="catalog-screen">
    <GuideView channels={channels} categories={categories} onPlay={onPlay} onLoadSchedules={onLoadSchedules} />
    <AdPlacement ad={homeAds.epg_sponsor} variant="wide" onVisible={markAdVisible} onSelect={onAdSelect} />
  </main>;

  return (
    <main className="content-shell" data-testid="catalog-screen">
      {screen === 'live' && <header className="topbar topbar--compact">
        <div><span className="eyebrow">INNOVA TV</span><h1>Canais ao vivo</h1><p>Escolha um canal e pressione Enter para assistir.</p></div>
      </header>}

      {screen === 'home' && <section
        className="hero"
        data-testid="home-hero"
        style={{ backgroundImage: `linear-gradient(90deg, rgba(10,10,12,.98) 0%, rgba(10,10,12,.72) 48%, rgba(10,10,12,.18) 100%), url("${heroChannel.background_image_web || heroChannel.background_image}")` }}
      >
        <div className="hero__content">
          <div className="hero__channel"><img src={heroChannel.image} alt="" /><span>Canal {heroChannel.channel_number}</span></div>
          <h2>{heroChannel.currentProgram?.name || heroChannel.name}</h2>
          <p>{heroChannel.description || 'Conteúdo ao vivo para você acompanhar agora.'}</p>
          <div className="hero__schedule">
            <strong>{shortTime(heroChannel.currentProgram?.time_start)}–{shortTime(heroChannel.currentProgram?.time_end)}</strong>
            {heroChannel.nextProgram && <span>A seguir: {heroChannel.nextProgram.name}</span>}
          </div>
          <div className="hero__actions">
            <button data-focusable="true" data-autofocus="true" data-content-autofocus="true" data-focus-zone="hero" data-focus-key="hero-watch" className="watch-button" onClick={() => onPlay(heroChannel)} data-testid="hero-watch">
              <Icon name="play" /> Assistir agora
            </button>
            <button data-focusable="true" data-focus-zone="hero" className="secondary-button" onClick={() => onNavigate('guide')}>Ver programação</button>
          </div>
        </div>
        {featuredChannels.length > 1 && <div className="hero-dots" aria-label="Destaques">{featuredChannels.map(channel => <button
          key={channel.id} data-focusable="true" data-focus-zone="hero" aria-label={`Destacar ${channel.name}`}
          className={channel.id === heroChannel.id ? 'active' : ''} onClick={() => setPreview(channel)} />)}</div>}
      </section>}

      {screen === 'home' && continueWatching.length > 0 && <section className="continue-section" data-testid="continue-watching">
        <header><div><span className="eyebrow">SUA LISTA</span><h2>Continuar assistindo</h2></div></header>
        <div className="continue-grid">{continueWatching.slice(0, 6).map(item => <button
          key={item.id} data-focusable="true" data-testid={`continue-${item.id}`} onClick={() => onResume(item)}
          data-focus-zone="continue" data-focus-key={`continue-${item.id}`}
        ><img src={item.artwork} alt="" loading="lazy" /><span className="continue-card__shade" /><span className="continue-card__copy"><strong>{item.name}</strong><small>{item.subtitle}</small><em>Continuar em {progressTime(item.position_seconds)}</em></span><span className="continue-progress"><i style={{ width: `${Math.min(item.progress_percent, 100)}%` }} /></span></button>)}</div>
      </section>}

      {screen === 'home' && <>
        <section className="home-section" data-testid="home-live-rail">
          <RailHeader eyebrow="NO AR AGORA" title="Canais ao vivo" action="Ver todos" onAction={() => onNavigate('live')} />
          <div className="home-channel-rail">{homeChannels.map(channel => <ChannelCard key={channel.id} channel={channel} onPlay={onPlay} onPreview={setPreview} />)}</div>
        </section>

        <AdPlacement ad={homeAds.home_section_banner_1} variant="wide" onVisible={markAdVisible} onSelect={onAdSelect} />

        <AdPlacement ad={primaryHeroAd} variant="hero" onVisible={markAdVisible} onSelect={onAdSelect} />

        {featuredMovies.length > 0 && <section className="home-section" data-testid="home-movies-rail">
          <RailHeader eyebrow="ESCOLHIDOS PARA VOCÊ" title="Filmes para assistir agora" action="Explorar filmes" onAction={() => onNavigate('movies')} />
          <div className="home-vod-rail">
            {featuredMovies.slice(0, 4).map(title => <HomeVodCard key={title.id} title={title} onSelect={onSelectVod} />)}
            <AdPlacement ad={homeAds.home_recommendation_card} variant="portrait" onVisible={markAdVisible} onSelect={onAdSelect} />
            {featuredMovies.slice(4, 8).map(title => <HomeVodCard key={title.id} title={title} onSelect={onSelectVod} />)}
            <AdPlacement ad={homeAds.home_card_portrait} variant="portrait" onVisible={markAdVisible} onSelect={onAdSelect} />
            {featuredMovies.slice(8, 12).map(title => <HomeVodCard key={title.id} title={title} onSelect={onSelectVod} />)}
            {homeAds.rail_sponsored_card && <SponsoredCard ad={homeAds.rail_sponsored_card} onVisible={markAdVisible} onSelect={onAdSelect} />}
          </div>
        </section>}

        <AdPlacement ad={homeAds.home_section_banner_2} variant="wide" onVisible={markAdVisible} onSelect={onAdSelect} />

        <AdPlacement ad={homeAds.home_banner_large} variant="large" onVisible={markAdVisible} onSelect={onAdSelect} />
        <AdPlacement ad={homeAds.home_card_landscape} variant="landscape" onVisible={markAdVisible} onSelect={onAdSelect} />

        {[homeAds.home_sponsored_rail, homeAds.home_collection_ad].filter((ad): ad is HomeAd => Boolean(ad?.collection)).map(ad => <section className={`home-sponsored-collection${ad.slot === 'home_sponsored_rail' ? ' home-sponsored-collection--rail' : ''}`} key={ad.id} data-testid={`home-ad-${ad.slot}`}>
          <span>CONTEÚDO PATROCINADO</span><button data-focusable="true" onClick={() => { markAdVisible(ad); setCollectionAd(ad); }}><img src={ad.image} alt={ad.title} /><strong>{ad.title}</strong></button>
        </section>)}

        {featuredSeries.length > 0 && <section className="home-section" data-testid="home-series-rail">
          <RailHeader eyebrow="HISTÓRIAS PARA MARATONAR" title="Séries em destaque" action="Explorar séries" onAction={() => onNavigate('series')} />
          <div className="home-vod-rail">
            {featuredSeries.slice(0, 4).map(title => <HomeVodCard key={title.id} title={title} onSelect={onSelectVod} />)}
            <AdPlacement ad={homeAds.home_card_compact} variant="compact" onVisible={markAdVisible} onSelect={onAdSelect} />
            {!featuredMovies.length && homeAds.rail_sponsored_card && <SponsoredCard ad={homeAds.rail_sponsored_card} onVisible={markAdVisible} onSelect={onAdSelect} />}
            {featuredSeries.slice(4).map(title => <HomeVodCard key={title.id} title={title} onSelect={onSelectVod} />)}
          </div>
        </section>}

        <AdPlacement ad={homeAds.home_section_banner_3} variant="wide" onVisible={markAdVisible} onSelect={onAdSelect} />

        <AdPlacement ad={homeAds.home_banner_wide} variant="wide" onVisible={markAdVisible} onSelect={onAdSelect} />
        <AdPlacement ad={homeAds.home_card_square} variant="square" onVisible={markAdVisible} onSelect={onAdSelect} />
      </>}

      {screen === 'live' && (
        <div className="category-row" aria-label="Categorias">
          {categories.slice(0, 8).map(item => (
            <button
              key={item.code}
              data-focusable="true"
              data-focus-zone="categories"
              data-content-autofocus={item.code === 'ALL' ? 'true' : undefined}
              data-testid={`category-${item.code}`}
              className={category === item.code ? 'active' : ''}
              onClick={() => setCategory(item.code)}
            >{item.name}</button>
          ))}
        </div>
      )}

      {screen === 'live' && <section className="channel-grid" aria-label="Canais">
        {visibleChannels.map(channel => <ChannelCard key={channel.id} channel={channel} onPlay={onPlay} onPreview={setPreview} />)}
      </section>}
    </main>
  );
}
